import { flowRight, isUndefined } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  HEADER_SHOW_ON_BLOG_HOMEPAGE_PATH,
  IS_BANNER_ENABLED_PATH,
  SECTION_HOMEPAGE,
} from '@wix/communities-blog-client-common/dist/src/constants/wix-params';
import { COVER_TYPE_IMAGE } from '@wix/communities-blog-client-common/dist/src/constants/cover-types';
import { HEADER_SHOW_DEFAULT } from '@wix/communities-blog-client-common/dist/src/constants/header-sizes';
import { IS_BANNER_ENABLED_DEFAULT } from '@wix/communities-blog-client-common/dist/src/constants/banner';
import fetchFeedPosts from '../../actions/fetch-feed-posts';
import { fetchLastFeaturedPostIfNeeded } from '../../actions/fetch-last-featured-post';
import { incrementPostLikeCount } from '../../actions/increment-post-like-count';
import { pageOpened } from '../../actions/page-opened';
import { setIsLoaded } from '../../actions/set-is-loaded';
import { waitForAppSettings } from '../../actions/fetch-app-settings';
import { FEED_PAGE } from '../../services/detect-route';
import { getAppData } from '../../selectors/app-data-selectors';
import { getConfig } from '../../services/config';
import { getRawOuterUrl } from '../../services/get-outer-url';
import { getPage, push } from '../../services/router-utils';
import { formatTitle } from '../../services/pagination';
import { scrollToAppTop } from '../../services/scroll-to';
import { getFeedJSONSchema, getWebsiteJSONSchema } from '../../services/json-schema';
import { getFeedPosts, getFeedPostsByPage } from '../../selectors/post-selectors';
import { getIsAppLoaded } from '../../selectors/is-app-loaded-selectors';
import { getIsFeedPostsLoaded } from '../../selectors/is-loaded-selectors';
import { getIsFeedPostsLoading } from '../../selectors/is-loading-selectors';
import { isOneApp } from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { isEditor, isSite } from '../../selectors/view-mode-selectors';
import { isExcludePostContentSupported } from '../../selectors/layout-selectors';
import { getCategoriesMap } from '../../selectors/categories-selectors';
import withAppSettings from '../../hoc/with-app-settings';
import withAuth from '../../hoc/with-auth';
import withDeviceType from '../../hoc/with-device-type';
import withCategoryTextAlignment from '../../hoc/with-category-text-alignment';
import withPaginationSettings, { getPaginationSettings } from '../../hoc/with-pagination-settings';
import withPermissions from '../../hoc/with-permissions';
import withTranslate from '../../hoc/with-translate';
import AccountSuspended from '../../components/account-suspended';
import AnimatedLoader from '../../components/animated-loader';
import AppLoaded from '../../components/app-loaded';
import CreatePostButtonMobile from '../../components/create-post-button-mobile';
import FeaturedPostBanner from '../featured-post-banner';
import NoPostsFeed from '../no-posts-feed';
import Page from '../../components/page';
import PageDescription from '../../components/page-description';
import PageMetadata from '../../components/page-metadata';
import Pagination from '../pagination';
import PostList from '../../components/post-list';
import JsonSchema from '../../components/json-schema';

export class FeedPage extends Component {
  static fetchData = ({ store, params, query }) => {
    return Promise.all([
      store.dispatch(
        waitForAppSettings(
          () =>
            fetchFeedPosts({
              page: getPage(params, query),
              excludeContent: isExcludePostContentSupported(store.getState()),
            }),
          true,
        ),
      ),
      store.dispatch(waitForAppSettings(fetchLastFeaturedPostIfNeeded, true)),
    ]).then(() => {
      const { showPagination, isValidPage } = getPaginationSettings(SECTION_HOMEPAGE, store.getState(), { params });
      if (showPagination && !isValidPage) {
        const error = { status: 404 };
        throw error;
      }
    });
  };

  state = {};

  constructor(props) {
    super(props);
    this.state = {
      isAnimated: !props.isLoaded,
    };
  }

  componentDidMount() {
    const { pageOpened, page, isAppLoaded, isMobile, isEditor } = this.props;

    pageOpened({ page: FEED_PAGE, isMobile, isEditor });
    if (isAppLoaded && page === 1) {
      this.fetchFeedPosts();
    }
  }

  shouldComponentUpdate(nextProps) {
    return !isUndefined(nextProps.entityCount);
  }

  componentDidUpdate({ pageSize, excludeContent }) {
    if (this.props.pageSize !== pageSize || (excludeContent && !this.props.excludeContent)) {
      this.fetchFeedPosts();
    }
  }

  fetchFeedPosts(page = this.props.page) {
    if (this.props.isBlocked) {
      return;
    }
    return this.props.fetchFeedPosts({ page, excludeContent: this.props.excludeContent });
  }

  changePage = page => {
    this.fetchFeedPosts(page);
    this.props.push({ pathname: this.createPagePath(page) });
    if (this.props.showPagination) {
      scrollToAppTop();
    }
  };

  createPagePath = page => (page > 1 ? `/page/${page}/` : '/');
  createPageUrl = page => getRawOuterUrl(this.createPagePath(page)).toString();

  loadMore = page => this.fetchFeedPosts(page);

  renderPosts() {
    const {
      isBlocked,
      currentPagePosts,
      posts,
      entityCount,
      page,
      isLoading,
      isLoaded,
      showCreatePostAction,
      isOneApp,
      showPagination,
    } = this.props;
    const hasPosts = posts.length;

    if (isBlocked) {
      return <AccountSuspended />;
    }

    if (isLoaded && !hasPosts) {
      return <NoPostsFeed />;
    }

    return (
      <PostList
        pageStart={page + 1}
        entityCount={entityCount}
        changePage={this.changePage}
        loadMore={showPagination ? undefined : this.loadMore}
        allPosts={showPagination ? currentPagePosts : posts}
        currentPagePosts={currentPagePosts}
        isLoading={isLoading}
        showCreatePostAction={showCreatePostAction && !isOneApp}
      />
    );
  }

  renderPagination() {
    const { entityCount, pageSize, page } = this.props;
    return (
      <Pagination
        page={page}
        pageSize={pageSize}
        entityCount={entityCount}
        onChange={this.changePage}
        createPageUrl={this.createPageUrl}
      />
    );
  }

  render() {
    const {
      isLoaded,
      appData,
      seoData,
      showHeader,
      homePageTextAlignment,
      isOneApp,
      showCreatePostAction,
      isBannerEnabled,
      showPagination,
      jsonSchemas,
      page,
      lastPage,
      t,
    } = this.props;

    const metadata = {
      image: showHeader && appData.coverType === COVER_TYPE_IMAGE ? appData.cover : null,
      prevUrl: showPagination && page > 1 ? this.createPageUrl(page - 1) : undefined,
      nextUrl: showPagination && page < lastPage ? this.createPageUrl(page + 1) : undefined,
    };

    if (showPagination && page > 1) {
      metadata.canonicalUrl = this.createPageUrl(page);
    }

    if (showPagination && seoData.title) {
      metadata.title = formatTitle({ title: seoData.title, page, lastPage, t });
    }

    return (
      <Page noSpacing={!isOneApp}>
        <PageMetadata key={lastPage} {...metadata} />
        {showHeader && (
          <PageDescription
            title={appData.label}
            description={appData.description}
            cover={appData.cover}
            coverType={appData.coverType}
            overlayColor={appData.overlayColor}
            backgroundColor={appData.backgroundColor}
            textColor={appData.textColor}
            headerSize={300}
            headerTextAlignment={homePageTextAlignment}
          />
        )}
        {isOneApp && showCreatePostAction ? <CreatePostButtonMobile /> : null}
        {isBannerEnabled && <FeaturedPostBanner />}
        <AnimatedLoader isLoading={!isLoaded} isAnimated={this.state.isAnimated}>
          {this.renderPosts()}
        </AnimatedLoader>
        {showPagination && this.renderPagination()}
        {isLoaded && <AppLoaded />}
        {jsonSchemas.map((jsonSchema, i) => (
          <JsonSchema key={i} content={jsonSchema} />
        ))}
      </Page>
    );
  }
}

FeedPage.propTypes = {
  fetchFeedPosts: PropTypes.func.isRequired,
  appData: PropTypes.object.isRequired,
  pageOpened: PropTypes.func.isRequired,
  incrementPostLikeCount: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  push: PropTypes.func,
  posts: PropTypes.array,
  currentPagePosts: PropTypes.array,
  category: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  params: PropTypes.object,
  t: PropTypes.func,
  newPostId: PropTypes.string,
  isLoading: PropTypes.bool,
  isLoaded: PropTypes.bool,
  showCreatePostAction: PropTypes.bool.isRequired,
  showHeader: PropTypes.bool.isRequired,
  homePageTextAlignment: PropTypes.number.isRequired,
  isAppLoaded: PropTypes.bool,
  isOneApp: PropTypes.bool.isRequired,
  isBannerEnabled: PropTypes.bool,
  excludeContent: PropTypes.bool,
  pageSize: PropTypes.number.isRequired,
  page: PropTypes.number,
  lastPage: PropTypes.number,
  entityCount: PropTypes.number,
  showPagination: PropTypes.bool,
  jsonSchemas: PropTypes.arrayOf(PropTypes.object),
  seoData: PropTypes.object,
  isMobile: PropTypes.bool,
  isEditor: PropTypes.bool,
};

FeedPage.defaultProps = {
  showHeader: HEADER_SHOW_DEFAULT,
  isBannerEnabled: IS_BANNER_ENABLED_DEFAULT,
};

const mapStateToProps = (state, ownProps) => {
  const currentPagePosts = getFeedPostsByPage(state, ownProps.page);
  const { sectionUrl, publisher, seoData = {} } = getConfig();

  return {
    appData: getAppData(state),
    seoData,
    posts: getFeedPosts(state),
    currentPagePosts,
    newPostId: state.newPostMessage,
    isLoading: getIsFeedPostsLoading(state),
    isLoaded: getIsFeedPostsLoaded(state),
    showCreatePostAction: isSite(state) && ownProps.canSee('create', 'post'),
    isAppLoaded: getIsAppLoaded(state),
    isOneApp: isOneApp(state),
    excludeContent: isExcludePostContentSupported(state),
    jsonSchemas: publisher
      ? [getWebsiteJSONSchema(sectionUrl), getFeedJSONSchema(state, currentPagePosts, getCategoriesMap(state))]
      : [],
    isEditor: isEditor(state),
  };
};

const mapDispatchToProps = {
  fetchFeedPosts,
  setIsLoaded,
  incrementPostLikeCount,
  pageOpened,
  push,
};

export default flowRight(
  withPermissions,
  withPaginationSettings(SECTION_HOMEPAGE),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withAuth,
  withDeviceType,
  withAppSettings({
    showHeader: HEADER_SHOW_ON_BLOG_HOMEPAGE_PATH,
    isBannerEnabled: IS_BANNER_ENABLED_PATH,
  }),
  withCategoryTextAlignment,
  withTranslate,
)(FeedPage);
